import { create } from 'zustand';
import api from '../config/axiosConfig';
import { PesacoreIHEngine } from '../engine/PesacoreIhEngine';
import messagerieSocket from '../socket/messagerieSocket';
import monitorSocket from '../socket/monitorSocket';

// ── Singletons socket ──────────────────────────────────────────────────────
let socketInitialized = false;
let socketCleanup     = null;
let currentListeners = null;

const usePesacoreIH = create((set, get) => ({

  // ── Moteur ─────────────────────────────────────────────────────────────
  engine: null,

  // ── État kernel (miroir du moteur, notifié via window.__PESACORE_IH_STORE__) ──
  kernelStatus:   'initializing',   // initializing | ready | degraded | locked
  dryRunMode:     false,
  pendingChanges: 0,
  systemHealth:   { score: 100, status: 'healthy', issues: [] },
  lastHealthCheck: null,

  // ── Métriques système ──────────────────────────────────────────────────
  systemMetrics: {
    cpu: 0, memory: 0, disk: 0, network: 0, queueDepth: 0,
  },

  // ── Services live ──────────────────────────────────────────────────────
  liveServices: [],
  diskLabel: '',

  // ── Logs système ──────────────────────────────────────────────────────
  systemLogs:    [],
  logsStreaming: true,

  // ── Alertes ────────────────────────────────────────────────────────────
  systemAlerts:   [],
  criticalAlerts: 0,

  // ── Instances (tenants) ────────────────────────────────────────────────
  instances:          [],
  totalInstances:     0,
  totalUsers:         0,
  pesasGlobalCirculating: 0,

  // ── Audit & Commandes ─────────────────────────────────────────────────
  auditLog:         [],
  commandHistory:   [],
  pendingMigrations: [],

  // ── UI state ───────────────────────────────────────────────────────────
  loading:        false,
  error:          null,
  socketConnected: false,

  // Messagerie interne Pesacore
  messagerieOpen:    false,
  messagerieRecipient: null,


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 1 : INITIALISATION
  // ═══════════════════════════════════════════════════════════════════════

  initialize: async (actor) => {
    set({ loading: true, error: null });
  
    try {
      // 1. Bridge store → moteur
      if (typeof window !== 'undefined') {
        window.__PESACORE_IH_STORE__ = {
          setState: (updates) => set(updates),
        };
      }
  
      // 2. Instancier le moteur
      const engine = new PesacoreIHEngine({
        actor: actor || { userId: 'superadmin', email: '', role: 'superadmin' },
      });
  
      // 3. Stats globales — l'API retourne soit { totalInstances, ... }
      //    soit { data: { totalInstances, ... } }, on gère les deux
      let globalStats = {};
      let kpis        = {};
      try {
        const statsRes = await api.get('/superadmin/stats');
        globalStats    = statsRes.data || {};
        kpis           = globalStats.kpis || {};
      } catch (e) {
        console.warn('[usePesacoreIH] Stats fetch failed (non-bloquant):', e.message);
      }
  
      // 4. Instances — l'API retourne { instances: [], summary: {}, pagination: {} }
      //    PanneauInstances fait r.data.instances, on aligne ici
      let instances = [];
      let instancesSummary = null;
      try {
        const instancesRes = await api.get('/superadmin/instances');
        const payload = instancesRes.data;
  
        // Robustesse : tableau direct OU objet { instances: [] }
        if (Array.isArray(payload)) {
          instances = payload;
        } else if (Array.isArray(payload?.instances)) {
          instances        = payload.instances;
          instancesSummary = payload.summary || null;
        }
      } catch (e) {
        console.warn('[usePesacoreIH] Instances fetch failed (non-bloquant):', e.message);
      }
  
      // 5. Nourrir le moteur
      engine.state.globalStats  = globalStats;
      engine.state.instances    = instances;
      engine.state.kernelStatus = 'ready';
  
      // 6. Hydrater le store — priorité aux stats globales, fallback sur instances locales
      set({
        engine,
        kernelStatus:           'ready',
        instances,
        pendingChanges:         globalStats.pendingChanges ?? 0,
        criticalAlerts:         globalStats.criticalAlerts ?? 0,
  
        // Compteurs — stats globales en priorité, fallback sur données instances
        totalInstances:         globalStats.totalInstances
                                  ?? instancesSummary?.total
                                  ?? instances.length
                                  ?? 0,
  
        totalUsers:             kpis.totalUsers
                                  ?? instancesSummary?.totalUsers
                                  ?? 0,
  
        pesasGlobalCirculating: kpis.totalPesas             ?? kpis.pesasGlobalCirculating ?? 0,
        
  
        loading: false,
      });

      try {
        const diskRes = await api.get('/superadmin/disk');
        const fs = diskRes.data?.filesystem;
      
        // totalBytes → GB utilisés (valeur réelle disponible)
        const totalBytes = fs?.totalBytes ?? 0;
        const totalGB    = totalBytes / (1024 ** 3); // bytes → GB
      
        if (totalGB > 0) {
          set(state => ({
            systemMetrics: { ...state.systemMetrics, disk: totalGB },
            // On stocke aussi le label formaté pour l'affichage
            diskLabel: fs?.totalLabel ?? `${totalGB.toFixed(2)} GB`,
          }));
        }
      } catch (_) {
      }

       try {
        if (messagerieSocket && !messagerieSocket.connected) {
          console.log('[usePesacoreIH] Initialisation socket messagerie pour rôle:', actor?.role);
          if (actor?.userId) {
            messagerieSocket.setUserId(actor.userId);
          }
          messagerieSocket.connect();
        } else if (messagerieSocket?.connected && actor?.userId) {
          messagerieSocket.setUserId(actor.userId);
          messagerieSocket.emit('joinUserRoom', actor.userId);
        }
      } catch (err) {
        console.warn('[usePesacoreIH] Messagerie socket init warning:', err.message);
      }
  
      console.log('[usePesacoreIH] Kernel initialized — instances:', instances.length, '| stats:', globalStats);
  
    } catch (error) {
      console.error('[usePesacoreIH] Init failed:', error);
      set({ error: error.message, loading: false, kernelStatus: 'degraded' });
    }
  },


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 2 : MONITORING SOCKET
  // ═══════════════════════════════════════════════════════════════════════

  startMonitoring: () => {
    if (socketInitialized) return;
    socketInitialized = true;

    // 1. On connecte d'abord
    monitorSocket.connect();

    // 2. On définit les handlers
    const onConnect = () => {
      console.log('[monitorSocket] Connecté');
      set({ socketConnected: true });
    };

    const onDisconnect = (reason) => {
      console.log('[monitorSocket] ❌ Déconnecté - raison:', reason);
      set({ socketConnected: false });
    };

    const onConnectError = (err) => {
      console.error('[monitorSocket] ❌ Erreur de connexion:', err.message);
      set({ socketConnected: false, error: err.message || 'Socket connection error' });
    };

    const onMetrics = (metrics) => {
      const normalized = {
        cpu:        metrics.cpu     ?? 0,
        memory:     metrics.memory  ?? 0,
        disk:       metrics.storage ?? 0,   // ← socket appelle ça "storage", vaut 0
        network:    metrics.network ?? 0,
        queueDepth: metrics.queueDepth ?? metrics.queue_depth ?? 0,
      };
    
      // Si le socket envoie storage=0, on ne l'écrase pas si on a déjà une valeur API
      set(state => ({
        systemMetrics: {
          ...normalized,
          // Préserve la valeur disk issue de l'API si le socket envoie 0
          disk: normalized.disk > 0 ? normalized.disk : state.systemMetrics.disk,
        },
      }));
    
      const { engine } = get();
      if (engine) {
        const health = engine.updateSystemMetrics(get().systemMetrics);
        set({ systemHealth: health, kernelStatus: engine.state.kernelStatus });
      }
    };

    const onServices = (services) => {
      set({ liveServices: services });
      const { engine } = get();
      if (engine) engine.updateServices(services);
    };

    const onLog = (entry) => {
      if (!get().logsStreaming) return;
      set(state => ({
        systemLogs: [
          { ...entry, timestamp: entry.timestamp || new Date().toISOString() },
          ...state.systemLogs.slice(0, 99),
        ],
      }));
    };

    const onAlert = (alert) => {
      const isCritical = ['critical', 'emergency'].includes(alert.severity);
      set(state => ({
        systemAlerts:   [alert, ...state.systemAlerts].slice(0, 100),
        criticalAlerts: isCritical ? state.criticalAlerts + 1 : state.criticalAlerts,
      }));
    };

    const onUserActivity = (activity) => {
      console.log('[MonitorSocket] user:activity reçu:', activity);
      
      const { engine } = get();
      
      // 1. Notifier le moteur (pour audit + stats)
      if (engine && engine.onUserActivity) {
        engine.onUserActivity(activity);
      }
      
      // 2. Afficher dans les logs système du superadmin
      set(state => ({
        systemLogs: [
          {
            level: 'INFO',
            msg: `👤 ${activity.type === 'register' ? 'Nouvelle inscription' : 'Connexion'} : ${activity.userId}${activity.tenantId ? ` (${activity.tenantId})` : ''}`,
            timestamp: activity.timestamp || new Date().toISOString(),
            metadata: { 
              userId: activity.userId, 
              tenantId: activity.tenantId,
              type: activity.type,
              ip: activity.ip 
            }
          },
          ...state.systemLogs.slice(0, 99)
        ]
      }));
    };
  
  

    monitorSocket.on('connect',        onConnect);
    monitorSocket.on('disconnect',     onDisconnect);
    monitorSocket.on('connect_error',  onConnectError);
    monitorSocket.on('system:metrics', onMetrics);
    monitorSocket.on('system:services',onServices);
    monitorSocket.on('system:log',     onLog);
    monitorSocket.on('system:alert',   onAlert);
    monitorSocket.on('user:activity', onUserActivity);

    currentListeners = {
      onConnect, onDisconnect, onConnectError, onMetrics, onServices, onLog, onAlert, onUserActivity
    };

    const syncState = () => {
      const shouldBeConnected = !!monitorSocket.connected;
      if (get().socketConnected !== shouldBeConnected) {
        console.log(`[monitorSocket] Sync état: ${get().socketConnected} → ${shouldBeConnected}`);
        set({ socketConnected: shouldBeConnected });
      }
    };

    // Exécute la synchro immédiatement (et après un petit délai)
    syncState();
    const immediateSync = setTimeout(syncState, 100);

    const heartbeat = setInterval(() => {
      if (monitorSocket.connected) monitorSocket.emit('superadmin:ping');
    }, 30000);

    socketCleanup = () => {
      clearTimeout(immediateSync);
      clearInterval(heartbeat);

      if (currentListeners) {
        const { onConnect, onDisconnect, onConnectError, onMetrics, onServices, onLog, onAlert } = currentListeners;

        monitorSocket.off('connect', onConnect);
        monitorSocket.off('disconnect', onDisconnect);
        monitorSocket.off('connect_error', onConnectError);
        monitorSocket.off('system:metrics', onMetrics);
        monitorSocket.off('system:services', onServices);
        monitorSocket.off('system:log', onLog);
        monitorSocket.off('system:alert', onAlert);
      }

      socketInitialized = false;
      currentListeners = null;
    };
  },

  stopMonitoring: () => {
    if (socketCleanup) socketCleanup();
    if (monitorSocket.connected) monitorSocket.disconnect();
    set({ socketConnected: false });
    
    if (currentListeners?.onUserActivity) {
      monitorSocket.off('user:activity', currentListeners.onUserActivity);
    }
  },


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 3 : COMMANDES ROOT (validation moteur → backend)
  // ═══════════════════════════════════════════════════════════════════════

  prepareAction: (actionType, payload) => {
    const { engine } = get();
    if (!engine) return { valid: false, errors: ['Moteur non initialisé'] };
    return engine.validateAndPrepare(actionType, payload);
  },

  executeAction: async (commandId, apiFn) => {
    const { engine } = get();
    if (!engine) return { success: false, error: 'Moteur non initialisé' };

    const result = await engine.executeCommand(commandId, apiFn);

    // Rafraîchir l'historique des commandes dans le store
    set({ commandHistory: engine.getCommandHistory(50) });

    return result;
  },

  // Toggle dry-run mode
  toggleDryRun: () => {
    const { engine } = get();
    if (!engine) return;
    const active = engine.toggleDryRun();
    set({ dryRunMode: active });
    return active;
  },


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 4 : ACTIONS SYSTÈME
  // ═══════════════════════════════════════════════════════════════════════

  runHealthCheck: async () => {
    const { engine } = get();
    try {
      const [apiResult, engineResult] = await Promise.all([
        api.get('/admin/health/full').then(r => r.data).catch(() => null),
        Promise.resolve(engine ? engine.runHealthCheck() : null),
      ]);

      const health = engineResult || apiResult;
      if (health) set({ systemHealth: health, lastHealthCheck: new Date().toISOString() });

      return health;
    } catch (err) {
      console.error('[usePesacoreIH] Health check failed:', err);
      return null;
    }
  },

  restartService: async (serviceId) => {
    const { prepareAction, executeAction } = get();

    // 1. Validation moteur
    const prep = prepareAction('RESTART_SERVICE', { serviceId });
    if (!prep.valid) return { success: false, errors: prep.errors };

    // 2. Exécution
    const result = await executeAction(prep.commandId, async () => {
      const res = await api.post(`/admin/system/services/${serviceId}/restart`);
      return res.data;
    });

    if (result.success) {
      set(state => ({
        liveServices: state.liveServices.map(s =>
          s.id === serviceId ? { ...s, status: 'healthy', load: 0 } : s
        ),
        systemLogs: [
          { time: new Date().toLocaleTimeString('fr-FR'), level: 'INFO', msg: `Service ${serviceId} redémarré`, timestamp: new Date().toISOString() },
          ...get().systemLogs.slice(0, 99),
        ],
      }));
    }

    return result;
  },

  flushGlobalCache: async () => {
    set({ loading: true });
    try {
      await api.post('/admin/cache/flush-learners');
      set(state => ({
        loading: false,
        systemLogs: [
          { time: new Date().toLocaleTimeString('fr-FR'), level: 'INFO', msg: 'Cache global vidé', timestamp: new Date().toISOString() },
          ...state.systemLogs.slice(0, 99),
        ],
      }));
      return { success: true };
    } catch (err) {
      set({ loading: false });
      return { success: false, error: err.message };
    }
  },

  runBackup: async () => {
    set({ loading: true });
    try {
      await api.post('/admin/backup/now');
      set({ loading: false });
      return { success: true };
    } catch (err) {
      set({ loading: false });
      return { success: false, error: err.message };
    }
  },

  restartAllServices: async () => {
    set({ loading: true });
    try {
      await api.post('/admin/services/restart');
      set({ loading: false });
      return { success: true };
    } catch (err) {
      set({ loading: false });
      return { success: false, error: err.message };
    }
  },


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 5 : MIGRATIONS & DÉPLOIEMENTS
  // ═══════════════════════════════════════════════════════════════════════

  registerMigration: (migration) => {
    const { engine } = get();
    if (!engine) return { success: false, errors: ['Moteur non initialisé'] };

    const result = engine.registerMigration(migration);
    if (result.success) {
      set({ pendingMigrations: engine.getPendingMigrations() });
    }
    return result;
  },

  deployMigration: async (migrationId) => {
    const { engine } = get();
    if (!engine) return { success: false, error: 'Moteur non initialisé' };

    const result = await engine.deployMigration(migrationId, async (mig) => {
      const res = await api.post('/superadmin/migrations/deploy', { migrationId, script: mig.script });
      return res.data;
    });

    set({
      pendingMigrations: engine.getPendingMigrations(),
      pendingChanges:    engine.state.pendingChanges,
    });

    return result;
  },

  rollbackMigration: async (migrationId) => {
    const { engine } = get();
    if (!engine) return { success: false, error: 'Moteur non initialisé' };

    const result = await engine.rollbackMigration(migrationId, async (snapshot) => {
      await api.post('/superadmin/migrations/rollback', { migrationId, snapshot });
    });

    return result;
  },


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 6 : INSTANCES (TENANTS)
  // ═══════════════════════════════════════════════════════════════════════

  loadInstances: async () => {
    set({ loading: true });
    try {
      const res     = await api.get('/superadmin/instances');
      const payload = res.data;
  
      const instances = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.instances)
          ? payload.instances
          : [];
  
      const total = payload?.summary?.total ?? instances.length;
  
      set({
        instances,
        totalInstances: total,
        loading: false,
      });
    } catch (err) {
      set({ loading: false, error: err.message });
    }
  },
 

  suspendInstance: async (tenantId) => {
    const { engine, instances } = get();
    const instance = instances.find(i => i.tenantId === tenantId);
    if (!instance) return { success: false, error: 'Instance introuvable' };

    // Validation moteur
    const check = engine?.validateInstanceOp(instance, 'suspend');
    if (check && !check.valid) return { success: false, errors: check.errors };

    try {
      await api.post(`/superadmin/instances/${tenantId}/suspend`);
      set(state => ({
        instances: state.instances.map(i =>
          i.tenantId === tenantId ? { ...i, status: 'suspended' } : i
        ),
      }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 7 : ALERTES
  // ═══════════════════════════════════════════════════════════════════════

  resolveAlert: async (alertId, notes = '') => {
    try {
      await api.post(`/admin/alerts/${alertId}/resolve`, { notes });
      set(state => {
        const alert = state.systemAlerts.find(a => a.id === alertId);
        const isCritical = alert && ['critical', 'emergency'].includes(alert.severity);
        return {
          systemAlerts:   state.systemAlerts.filter(a => a.id !== alertId),
          criticalAlerts: isCritical ? Math.max(0, state.criticalAlerts - 1) : state.criticalAlerts,
        };
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  ignoreAlert: async (alertId, reason = '') => {
    try {
      await api.post(`/admin/alerts/${alertId}/ignore`, { reason });
      set(state => ({
        systemAlerts: state.systemAlerts.filter(a => a.id !== alertId),
      }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 8 : AUDIT & LOGS
  // ═══════════════════════════════════════════════════════════════════════

  refreshAuditLog: (filters, limit = 100) => {
    const { engine } = get();
    if (!engine) return;
    set({ auditLog: engine.getAuditLog(filters, limit) });
  },

  toggleLogStream: () => set(state => ({ logsStreaming: !state.logsStreaming })),
  clearLogs:       () => set({ systemLogs: [] }),


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 9 : MESSAGERIE INTERNE PESACORE
  // ═══════════════════════════════════════════════════════════════════════

  openMessagerie: (recipient = null) => set({ messagerieOpen: true, messagerieRecipient: recipient }),
  closeMessagerie: ()                => set({ messagerieOpen: false, messagerieRecipient: null }),

  sendInternalMessage: async (subject, body, recipientIds = []) => {
    try {
      const payload = {
        to:          recipientIds,
        subject,
        body,
        messageType: 'conversation',
        metadata:    { priority: 'high', sentFrom: 'PesacoreIH', encrypted: false },
        attachments: [],
      };
      messagerieSocket.sendMessage(payload);
      set({ messagerieOpen: false, messagerieRecipient: null });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },


  // ═══════════════════════════════════════════════════════════════════════
  // CATÉGORIE 10 : QUOTA CHECK DIRECT (sans commande pipeline)
  // ═══════════════════════════════════════════════════════════════════════

  checkInstanceQuota: (plan, resource, currentCount) => {
    const { engine } = get();
    if (!engine) return { valid: true };
    return engine.checkQuota(plan, resource, currentCount);
  },


  // ═══════════════════════════════════════════════════════════════════════
  // UTILITAIRES
  // ═══════════════════════════════════════════════════════════════════════

  getKernelStatus: () => {
    const { engine } = get();
    return engine ? engine.getKernelStatus() : null;
  },

  getMetricHistory: (metric, points = 30) => {
    const { engine } = get();
    return engine ? engine.getMetricHistory(metric, points) : [];
  },

  destroyEngine: () => {
    const { engine } = get();
    if (engine) engine.destroy();
    if (typeof window !== 'undefined') delete window.__PESACORE_IH_STORE__;
    set({ engine: null, kernelStatus: 'initializing' });
  },

}));

export default usePesacoreIH;